import clsx from 'clsx';
import {
  type ReactElement,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { Text } from '../../typography/text/Text.js';
import {
  disabledTabWrapperCSS,
  tabButtonCSS,
  tabContentWrapperCSS,
} from './RenderTab.sty.js';
import { TabProps } from './Tab.js';

interface RenderTabProps {
  tabItem: ReactElement<TabProps>;
  /** @defaultValue false */
  isDisabled?: boolean;
  isSelected?: boolean;
  ariaControls?: string;
  onActivate?: () => void;
  onKeyDown?: (event: ReactKeyboardEvent) => void;
}

/**
 * actually rendered tab component
 */
export const RenderTab = (props: RenderTabProps) => {
  const {
    isDisabled: propsDisabled = false,
    isSelected = false,
    tabItem,
    ariaControls,
    onActivate,
    onKeyDown,
  } = props;

  const ref = useRef<HTMLButtonElement>(null);
  const { key, props: tabProps } = tabItem;
  const {
    title,
    disabled,
    children,
    prefixIcon,
    id = key ?? undefined,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    keepMounted,
    ...restProps
  } = tabProps;

  const isDisabled = propsDisabled || (disabled ?? false);

  const { focusProps, focusClassName } = useFocusRing({
    color: !isDisabled && isSelected ? 'primary' : 'neutral',
    disabled: isDisabled,
  });

  return (
    <div className={clsx({ [disabledTabWrapperCSS]: isDisabled })}>
      <button
        data-testid={dataTestId}
        {...restProps}
        ref={ref}
        type="button"
        data-key={key}
        role="tab"
        id={id}
        disabled={isDisabled}
        aria-selected={isSelected}
        aria-controls={ariaControls}
        {...(!isDisabled &&
          !propsDisabled && {
            onClick: onActivate,
            onKeyDown,
            onBlur: focusProps.onBlur,
            onFocus: focusProps.onFocus,
          })}
        className={clsx(
          tabButtonCSS({
            disabled: isDisabled,
            selected: isSelected,
          }),
          focusClassName,
          consumerClassName,
        )}
        style={consumerStyle}
      >
        <div className={tabContentWrapperCSS}>
          {prefixIcon !== undefined && prefixIcon}
          <Text className="text-content" as="div" textStyle="base">
            {title}
          </Text>
        </div>
      </button>
    </div>
  );
};
